import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from "styled-components";
import { WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import { Moment } from "moment";
import Colours from "../../../../atoms/Colours";
import { getWhereaboutsInfo, WhereaboutsDayInfo } from '@features/whereabouts/services/WhereaboutsInfoService';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { Center } from "@shared/ui/atoms/StructuralLayout";
import { SimpleSpinner } from "@shared/ui/atoms/SimpleSpinner";
import { BodySmall } from "@shared/ui/atoms/fonts/Body";

export function WhereaboutsHoverInfo(props: Props) {
  const {date, userId} = props;
  const dateString = date.format(DATE_FORMAT);
  const [loading, setLoading] = useState(false);
  const [whereaboutsInfo, setWhereaboutsInfo] = useState<WhereaboutsDayInfo | undefined>();
  const [left, setLeft] = useState(CELL_INSET);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadWhereaboutsInfo = async () => {
      setLoading(true);
      try {
        const info = await getWhereaboutsInfo(dateString, userId);
        setWhereaboutsInfo(info);
      } finally {
        setLoading(false);
      }

    }

    timerRef.current = setTimeout(() => loadWhereaboutsInfo(), 500);
  }, [dateString, userId]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    }
  }, []);

  // Keep the popup inside the viewport. Measures the day cell rather than the popup,
  // because the popup moves when repositioned and would undo its own correction.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const cell = container?.offsetParent;
    if (!container || !cell) return;
    const cellLeft = cell.getBoundingClientRect().left + cell.clientLeft;
    const maxLeft = document.documentElement.clientWidth - VIEWPORT_MARGIN - container.offsetWidth;
    const viewportLeft = Math.max(VIEWPORT_MARGIN, Math.min(cellLeft + CELL_INSET, maxLeft));
    setLeft(viewportLeft - cellLeft);
  }, [whereaboutsInfo, loading]);

  const hasOptionItemInfo = !!whereaboutsInfo?.amOptionItemLabel || !!whereaboutsInfo?.pmOptionItemLabel;
  if ((!loading && !whereaboutsInfo) || (!whereaboutsInfo?.label && !whereaboutsInfo?.title && !hasOptionItemInfo)) {
    return null;
  }

  const optionItemsMatch = whereaboutsInfo?.amOptionItemLabel === whereaboutsInfo?.pmOptionItemLabel;

  return (
    <Container ref={containerRef} style={{left}}>
      {(loading || !whereaboutsInfo) ? <>
        <Center>
          <SimpleSpinner size={22} />
        </Center>
      </> : <>
        <BodySmall weight={600}>{whereaboutsInfo.title}</BodySmall>
        <BodySmall>{whereaboutsInfo.label}</BodySmall>
        {hasOptionItemInfo && (optionItemsMatch ?
          <BodySmall>{whereaboutsInfo.amOptionItemLabel}</BodySmall> : <>
            {!!whereaboutsInfo.amOptionItemLabel && <BodySmall>AM: {whereaboutsInfo.amOptionItemLabel}</BodySmall>}
            {!!whereaboutsInfo.pmOptionItemLabel && <BodySmall>PM: {whereaboutsInfo.pmOptionItemLabel}</BodySmall>}
          </>)}
      </>}
    </Container>
  )
}

interface Props {
  amStatus?: WhereaboutsOption;
  pmStatus?: WhereaboutsOption;
  date: Moment;
  userId: string;
}

// Gap between the day cell's left edge and the popup when it fits
const CELL_INSET = 10;
// Smallest gap kept between the popup and either edge of the screen
const VIEWPORT_MARGIN = 8;

const Container = styled.div`
  position: absolute;
  z-index: 999999;
  min-width: 200px;
  max-width: 100%;
  min-height: 40px;
  background-color: white;
  top: 55px;
  border-radius: 8px;
  border: 1px solid ${Colours.veryLightGrey};
  box-shadow: 0 0 17px 2px rgba(0, 87, 255, 0.15);
  padding: 4px 8px;
`
